// ── Constants ──────────────────────────────────────────────────────────────

export const POSITIONS = [
  { id:"P",  label:"Pitcher",      short:"P",  x:50, y:60 },
  { id:"C",  label:"Catcher",      short:"C",  x:50, y:80 },
  { id:"1B", label:"First Base",   short:"1B", x:78, y:62 },
  { id:"2B", label:"Second Base",  short:"2B", x:62, y:41 },
  { id:"3B", label:"Third Base",   short:"3B", x:20, y:62 },
  { id:"SS", label:"Shortstop",    short:"SS", x:38, y:41 },
  { id:"LF", label:"Left Field",   short:"LF", x:20, y:24 },
  { id:"CF", label:"Center Field", short:"CF", x:50, y:16 },
  { id:"RF", label:"Right Field",  short:"RF", x:80, y:24 },
];

export const THROW_TARGETS = [
  { id:"P",  label:"Pitcher",   short:"P",  x:50, y:60 },
  { id:"C",  label:"Catcher",   short:"C",  x:50, y:80 },
  { id:"1B", label:"1st Base",  short:"1B", x:78, y:62 },
  { id:"2B", label:"2nd Base",  short:"2B", x:62, y:41 },
  { id:"3B", label:"3rd Base",  short:"3B", x:20, y:62 },
  { id:"SS", label:"Shortstop", short:"SS", x:38, y:41 },
];

export const INNINGS_OPTS = [
  { innings:6, level:"intermediate", tag:"Little League" },
  { innings:7, level:"advanced",     tag:"High School"   },
];

export const OUTWORDS        = ["No outs","One out","Two outs"];
export const CONFETTI_COLORS = ["#1FD71D","#231F20","#4caf50","#ffffff","#8fe39c","#5fcf70","#81c784","#14a616"];
export const BALL_BURST      = [
  {vx:-200,vy:-180,delay:0,   size:26},{vx:-130,vy:-260,delay:0.04,size:22},
  {vx: -50,vy:-290,delay:0.08,size:28},{vx:  30,vy:-290,delay:0.08,size:22},
  {vx: 110,vy:-260,delay:0.04,size:26},{vx: 190,vy:-180,delay:0,   size:24},
  {vx: -90,vy:-240,delay:0.12,size:20},{vx:  70,vy:-240,delay:0.12,size:20},
];

export const inputStyle = {
  width:"100%", boxSizing:"border-box", padding:"11px 13px", marginBottom:10,
  fontSize:16, borderRadius:7, border:"1.5px solid #ddd", outline:"none",
  fontFamily:"Georgia, serif", display:"block",
};

// ── Position Tips ──────────────────────────────────────────────────────────

export const POSITION_TIPS = {
  P:    ["After a ground ball to the right side, sprint to cover first base every time.",
         "Always back up third base on a hit to the outfield when there's a runner on first.",
         "On a bunt, communicate early — call 'Mine!' and field it decisively.",
         "After a strikeout with runners on base, look the runners back before relaxing.",
         "You are an infielder — be ready to field anything hit your way."],
  C:    ["Before every pitch, know the outs and runners — communicate to all your teammates.",
         "Block every ball in the dirt with your body. Protect home plate every pitch.",
         "After a strikeout, check if the batter might run on a wild pitch.",
         "On a pop-up near home, rip your mask off, point, and let the best-angle fielder call it.",
         "You are the quarterback — call the cutoffs and tell outfielders where to throw."],
  "1B": ["On a ball to your left, field it and flip to the pitcher covering the bag.",
         "Stretch toward every throw but keep your foot on the bag until the last second.",
         "On a bunt, call 'Pitcher!' and hustle back to your bag to take the throw.",
         "Hold runners close with a good target — your pitcher needs to see your glove.",
         "On a ground ball to you with a runner on first, tag the bag — don't chase the runner."],
  "2B": ["Break to second base the moment a ground ball is hit to the shortstop — the bag needs covering.",
         "Decide with your shortstop before the pitch who covers second on a steal. Communicate!",
         "Cover first base on any bunt or ground ball to the right side when the first baseman charges.",
         "On a pop-up, call it early and loud — let everyone know who has it.",
         "Turn double plays by giving your shortstop a low chest target at the bag."],
  "3B": ["On a bunt, charge hard and make a quick decision — throw to first for the out.",
         "With two outs, all runners go on any ground ball — force plays are your best friend.",
         "Know whether it is a force play or a tag play before every single pitch.",
         "After fielding a ball with no other play, get it to first base for the routine out.",
         "Back up second base on infield throws from the right side when you can."],
  SS:   ["The shortstop is the captain of the infield — talk to every player before every pitch.",
         "Call off the second baseman on any ball you can reach — the better angle wins.",
         "On a double play, get to the bag early and give your second baseman a clear chest-high target.",
         "In the hole between third and short, plant your feet and fire — don't hesitate.",
         "Back up second base on steal attempts when the second baseman takes the throw."],
  LF:   ["After catching a fly ball with fewer than two outs, look home — is the runner tagging up?",
         "Always hit your cutoff man. An accurate throw to the cutoff beats a wild throw home.",
         "Back up third base on any ground ball hit to the left side of the infield.",
         "Communicate with center field — call off CF if you have a better angle on the ball.",
         "After making a catch with no play, fire it to your cutoff man right away. Never hold it."],
  CF:   ["Center field is your outfield — you have priority over left and right fielders.",
         "After catching a fly ball, look for a play immediately and hit your cutoff man.",
         "Back up second base any time there is an infield throw that might get away.",
         "Know the situation before every pitch — outs, runners, and score change your first step.",
         "Communicate early and loud: call 'Mine!' before any ball you are going to catch."],
  RF:   ["Back up first base on every ground ball and passed ball — this prevents more runs than you think.",
         "Hit your cutoff man instead of throwing directly home — a relay is faster than an off-line throw.",
         "Communicate with center field on any ball in the gap. Call it early.",
         "After catching a fly ball, check third base — is that runner tagging up to score?",
         "A strong throw to first on a single can double off a runner who rounded too far."],
};

// ── Situation Types ────────────────────────────────────────────────────────
// Tags used to classify each scenario's core defensive decision, so
// weakness tracking can aggregate accuracy by *type of play* rather than
// just by position. Applied to question-bank docs via audit/tag-questions.js
// and offered as an optional field when coaches build custom scenarios.

export const SITUATION_TYPES = [
  { id:"force_play",     label:"Force Play" },
  { id:"tag_play",       label:"Tag Play" },
  { id:"double_play",    label:"Double Play" },
  { id:"bunt_coverage",  label:"Bunt Coverage" },
  { id:"cutoff_relay",   label:"Cutoff / Relay" },
  { id:"tag_up_fly",     label:"Fly Ball / Tag-Up" },
  { id:"pop_up_call",    label:"Pop-Up Communication" },
  { id:"covering_base",  label:"Covering a Base" },
  { id:"holding_runners",label:"Holding Runners" },
  { id:"backing_up",     label:"Backing Up a Play" },
  { id:"routine_play",   label:"Routine Play" },
];

// ── Badge Definitions ──────────────────────────────────────────────────────

export const BADGE_DEFINITIONS = [
  { id:"first_five",     icon:"✋", name:"First Five",     desc:"Complete your first 5 questions"       },
  { id:"hot_streak",     icon:"🔥", name:"On Fire",        desc:"5 correct answers in a row"             },
  { id:"iron_glove",     icon:"🧤", name:"Iron Glove",     desc:"10 correct answers in a row"            },
  { id:"clutch",         icon:"💪", name:"Clutch",         desc:"Correct in a final inning, close game"  },
  { id:"sharpshooter",   icon:"🎯", name:"Sharpshooter",  desc:"80%+ accuracy over 20+ questions"       },
  { id:"century",        icon:"💯", name:"Century Club",   desc:"Answer 100 total questions"             },
  { id:"utility_player", icon:"⚾", name:"Utility Player", desc:"Practiced all 9 positions"              },
  { id:"dedicated",      icon:"📅", name:"Dedicated",      desc:"Practice 3 days in a row"               },
];
